import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation } from '@apollo/client/react';

import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import {
    CreateOfferEventEnrollmentsDocument,
    IContentTypeValue,
    IOfferEventDetailFragment,
} from 'graphql/types';
import { UserSelectDrawer } from 'user/components/UserSelector/UserSelectDrawer';

interface IProps {
    groupId?: string;
    offerEvent: IOfferEventDetailFragment;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const EnrollParticipantsDrawer = ({
    groupId,
    offerEvent,
    open,
    onOpenChange,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const {
        id: offerEventId,
        accessTrainings,
        accessExternalContents,
        accessCourses,
        accessScorms,
    } = offerEvent;

    const includeProgress =
        !!accessTrainings.edges.length ||
        !!accessExternalContents.edges.length ||
        !!accessCourses.edges.length ||
        !!accessScorms.edges.length;

    const [createEnrollments, { loading: loadingAddUsers }] = useMutation(
        CreateOfferEventEnrollmentsDocument
    );

    const handleAddUsers = useCallback(
        async (userIds: string[]) => {
            try {
                await createEnrollments({
                    variables: {
                        offerEventId,
                        users: userIds,
                        includeProgress,
                        groupId,
                    },
                });
            } catch (error) {
                showApolloError(error);

                return;
            }

            client.cache.evict({ fieldName: 'paginatedOfferEvents' });
            client.cache.evict({ fieldName: 'paginatedUsers' });
            client.cache.evict({
                id: client.cache.identify(offerEvent),
                broadcast: true,
            });
            client.cache.gc();

            displaySnackbar(
                translate('trainingParticipants.participantsAddedSuccess', {
                    count: userIds.length,
                }),
                { variant: 'success' }
            );

            onOpenChange(false);
        },
        [
            client,
            createEnrollments,
            displaySnackbar,
            groupId,
            includeProgress,
            offerEvent,
            offerEventId,
            onOpenChange,
            showApolloError,
            translate,
        ]
    );

    return (
        <UserSelectDrawer
            loading={loadingAddUsers}
            open={open}
            selectorQueryVariables={{
                contentType: IContentTypeValue.OfferEvent,
                objectId: offerEventId,
            }}
            onAddUsers={handleAddUsers}
            onDrawerOpen={onOpenChange}
        />
    );
};
